<template>
    <div :class="['table-dashboard-dataview', { 'spinner-is-visible': isSpinnerVisible }]" @scroll.passive="onScroll" ref="tableWrapper" :style="{ width: gridWidth + 'px', height: gridHeight + 'px' }">
      <table 
        v-if="visibleSortedItems.length > 0"
        class="table table-striped"
      >
        <thead>
          <tr>
            <th 
              v-for="column in columns"
              :key="column.field"
              :class="[{'text-end': column.align === 'right'}, {'text-center': column.align === 'center'}, {'vue-sorter': column.sortable}, orderCss(column.field)]"
              :style="`width: ${column.width}px`"
              @click="column.sortable ? reorder(column.field) : null"
            >
              {{ column.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, index) in visibleSortedItems"
            :key="index"
            :class="{'is-marked': isItemMarked(item._record_id)}"
          >
            <td 
              v-for="column in columns" 
              :key="column.field"
              :class="[{'text-end': column.align === 'right'}, {'text-center': column.align === 'center'}]"
            >
              <template v-if="column.showDetails">
                <div 
                  class="btn-details btn-details-details"
                  @click="showAssetDetails(item)"
                >
                  <div title="Details" class="btn-details-icon"></div>
                  <div class="btn-details-content">
                    {{ column.method ? this[column.method](item[column.field]) : item[column.field] }}
                  </div>
                </div>
              </template>
              <template v-else-if="column.computed === 'hasPhotos'">
                <span 
                  v-if="hasPhotos(item)" 
                  class="indicator-images"
                  @click="showAssetDetails(item)"
                ></span>
              </template>
              <template v-else>
                <span>{{ column.method ? this[column.method](item[column.field]) : item[column.field] }}</span>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="spinner"></div>

      <div
        v-if="items.length === 0 && !isSpinnerVisible"
        class="grid-info-no-records"
        :class="{ 'hidden': isSpinnerVisible === false }"
      >According to the search criteria, there are no records found.</div>
    </div>
</template>

<script>
import CircleChart from "../../shared/stats/circle_chart";

import Helpers from "../../../helpers.js";

export default {
  components: {
    CircleChart
  },

  props: {
    columns: {
      type: Array,
      required: true
    },
    items: {
      type: Array,
      required: true
    },
    gridHeight: {
      type: Number,
      default: 200
    },
    gridWidth: {
      type: Number,
      default: 500
    },
    pageSize: {
      type: Number,
      default: 500
    },
    isInitialized: {
      type: Boolean,
      default: false
    },
    isSpinnerVisible: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      sortAsc: true,
      sortBy: this.columns[0]?.field || '', // default to first column
      visibleCount: this.pageSize,
      markedRows: []
    };
  },

  setup () {
    return {
      h$: Helpers
    }
  },

  watch: {
    items: {
      deep: true,
      handler(items) {
        // console.log("Grid Items changed:", items);
      }
    }
  },

  computed: {
    sortedItems() {
      if (!this.items || !this.sortBy) return this.items;
      return [...this.items].sort((a, b) => {
        if (a[this.sortBy] == null) return 1;
        if (b[this.sortBy] == null) return -1;
        if (a[this.sortBy] === b[this.sortBy]) return 0;
        if (this.sortAsc) {
          return a[this.sortBy] > b[this.sortBy] ? 1 : -1;
        } else {
          return a[this.sortBy] < b[this.sortBy] ? 1 : -1;
        }
      });
    },
    visibleSortedItems() {
      return this.sortedItems.slice(0, this.visibleCount);
    }
  },

  methods: {
    markRow: function(row) {
      this.markedRows = [];

      this.markedRows.push(row);
    },

    unmarkRows: function() {
      this.markedRows = [];
    },

    isItemMarked: function(item) {
      return this.markedRows.includes(item);
    },
    
    formatMaintenanceIssues(issues) {
      if (!issues && issues !== 0) return '';

      // Normalize to string and split on '||' (with optional spaces)
      const rawParts = Array.isArray(issues)
        ? issues
        : String(issues).split(/\s*\|\|\s*/);

      const seen = new Set();
      const out = [];

      rawParts.forEach(part => {
        if (part == null) return;

        // Split on commas (some entries are comma-separated)
        const commaParts = String(part).split(',');

        commaParts.forEach(p => {
          // remove prefix up to first ':' (e.g. 'Hardware Problems: Slack Guy' -> 'Slack Guy')
          const cleaned = p.includes(':') ? p.replace(/^[^:]*:\s*/, '') : p;
          const trimmed = cleaned.trim();
          if (trimmed.length === 0) return;
          if (!seen.has(trimmed)) {
            seen.add(trimmed);
            out.push(trimmed);
          }
        });
      });

      return out.join(', ');
    },

    showAssetDetails: function(asset) {
      const vmGrid = this;

      // Create a mapping of statuses to ratings
      const statusRatingMap = Object.entries(vmGrid.h$.fulcrumStatusRatingMap).reduce((acc, [rating, statuses]) => {
        statuses.forEach(status => acc[status] = Number(rating));
        return acc;
      }, {});
      
      const assetDetails = {
        recordID: asset.recordID,
        fulcrumRecordID: asset._record_id,
        title: asset._title,
        latitude: asset._latitude,
        longitude: asset._longitude,
        rating: statusRatingMap[asset._status] || 0,
        inspectionDate: asset.inspection_date,
        inspectionType: asset.inspection_type,
        commentSummary: asset.comment_summary,
        nearestAddress: asset.nearest_address,
        hasPhotos: this.hasPhotos(asset),
        // Woodpole-specific fields
        poleNumber: asset.pole_number,
        species: asset.species,
        class: asset.class,
        length: asset.length,
        damagedecayPresent: asset.damagedecay_present,
        year: asset.year,
        manufacturer: asset.manufacturer,
        maintenanceIssues: asset.maintenance_issues,
        // Padmount-specific fields
        facilityID: asset.facility_id,
        padInspectionType: asset.pad_inspection_type,
        cabinetCorrosion: asset.cabinet_corrosion,
        cabinetDent: asset.cabinet_dent,
        cabinetErosion: asset.cabinet_erosion,
        cabinetLeakingOil: asset.cabinet_leaking_oil,
        padPadIssues: asset.pad_pad_issues,
        padPadIssuesHoleSize: asset.pad_pad_issues_hole_size,
        padPadIssuesNumberOfHoles: asset.pad_pad_issues_number_of_holes,
        padmountIssues: asset.padmount_issues,
        groundMountedEquipmentType: asset.ground_mounted_equipment_type
      };

      vmDetailsPane.$refs.detailsPane.open({
        title: "Asset Details",
        entityType: "fulcrumAssetDetails",
        args: assetDetails
      });

      this.markRow(asset._record_id);
    },

    reorder(field) {
      if (this.sortBy === field) {
        this.sortAsc = !this.sortAsc;
      } else {
        this.sortBy = field;
        this.sortAsc = true;
      }
    },

    orderCss(field) {
      if (this.sortBy == field) {
        return 'sort-order-' + (this.sortAsc ? 'asc' : 'desc')
      }
    },

    onScroll(e) {
      const el = this.$refs.tableWrapper;
      if (!el) return;
      // If scrolled near bottom, load more
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
        if (this.visibleCount < this.sortedItems.length) {
          this.visibleCount += this.pageSize;
        }
      }
    },

    hasPhotos(item) {
      return Object.keys(item || {}).some(key => {
        return /_photos?$/.test(key) && Boolean(item[key]);
      });
    }
  }
};
</script>
